import { getProperty } from '../objectAccessors';
import { hasProperty } from 'customer-data-objects/model/ImmutableModel';
import { DESCRIPTION, PRICE } from 'customer-data-objects/lineItem/PropertyNames';
import { INTEGRATION_SOURCES, INTEGRATION_IDENTIFIERS, PRODUCT_SHOPIFY_IDENTIFIER_PROPERTY, LINE_ITEM_SHOPIFY_IDENTIFIER_PROPERTY, DEAL_SHOPIFY_IDENTIFIER_PROPERTY } from 'products-ui-components/constants/Integrations';
import { ECOMM_SYNCED, EXTERNAL_ACCOUNT_ID, EXTERNAL_APP_ID } from 'customer-data-objects/product/ProductProperties';
import DealRecord from 'customer-data-objects/deal/DealRecord';
import enviro from 'enviro';
import { QUICKBOOKS_APP_IDS, SHOPIFY_APP_IDS } from '../../constants/Integrations';
import { getObjectType, getSubject } from '../subject';
import { LINE_ITEM, PRODUCT } from 'customer-data-objects/constants/ObjectTypes';

/**
 * "Bad data" means the line item has `hs_sync_amount`.
 * It is bad data because `hs_sync_amount` short circuits the `amount` calculation
 * and becomes `amount`. This means that updates to properties like `price` and `discount`
 * do not affect `amount` when `hs_sync_amount` is set on the object.
 *
 * @TODO remove hasBadAmount once https://git.hubteam.com/HubSpot/IntegrationsPlatform-Sync/issues/1191
 * is done and hs_sync_amount is backfilled removed from all LIs
 *
 * @param {ObjectRecord<BaseObjectRecordContents>} lineItem
 * @returns {boolean} whether lineItem has `hs_sync_amount` value
 */
export function hasBadAmount(maybeLineItem) {
  const lineItem = getSubject(maybeLineItem);
  const hsSyncAmount = getProperty(lineItem, 'hs_sync_amount');
  return !['', null, undefined].includes(hsSyncAmount);
}
export function sourceIsHubSpot(maybeSubject) {
  const subject = getSubject(maybeSubject);
  return !getProperty(subject, EXTERNAL_ACCOUNT_ID) &&
  // keeping for backward compatibility until quick followup to prevent bugs
  !getProperty(subject, ECOMM_SYNCED);
}
export function sourceIsShopify(maybeSubject) {
  if (maybeSubject instanceof DealRecord) {
    return hasProperty(maybeSubject, DEAL_SHOPIFY_IDENTIFIER_PROPERTY);
  }
  const subject = getSubject(maybeSubject);
  const objectType = getObjectType(maybeSubject);
  if (objectType === PRODUCT) {
    return PRODUCT_SHOPIFY_IDENTIFIER_PROPERTY in subject.properties;
  } else if (objectType === LINE_ITEM) {
    return LINE_ITEM_SHOPIFY_IDENTIFIER_PROPERTY in subject.properties;
  }
  return false;
}

/**
 * For ecomm tracking: https://git.hubteam.com/hubspotprotected/settings-ui-products/issues/698
 * Shopify or QBO are considered native integrations; other externally synced
 * objects must be from ecomm developers
 *
 * @param {ProductRecord | LineItemRecord | DealRecord} subject
 * @returns {boolean} whether the object was synced from a non-native integration
 */
export function sourceIsEcommBridge(maybeSubject) {
  const env = enviro.isQa() ? 'QA' : 'PROD';
  const subject = getSubject(maybeSubject);
  const appId = getProperty(subject, EXTERNAL_APP_ID);
  return !sourceIsHubSpot(subject) && appId !== SHOPIFY_APP_IDS[env] && appId !== QUICKBOOKS_APP_IDS[env];
}

/**
 * @deprecated
 * used thoroughly in crm_ui to determine the editability of deals and cards
 * will be removable when we start unlocking synced objects
 * first we'll be able to unlock ecomm, then shopify objects
 */
export function getDealSource(deal) {
  if (deal.properties.has(DEAL_SHOPIFY_IDENTIFIER_PROPERTY)) {
    return INTEGRATION_SOURCES.SHOPIFY;
  } else if (deal.properties.has(INTEGRATION_IDENTIFIERS.ECOMM)) {
    return INTEGRATION_SOURCES.ECOMM_API;
  }
  return INTEGRATION_SOURCES.HUBSPOT;
}

/**
 * @deprecated
 * Keeping this getter around until issues with price can be resolved https://hubspot.slack.com/archives/C014ZCZ8H45/p1599672070034200?thread_ts=1595856593.006300&cid=C014ZCZ8H45
 */
export function getProductDescriptionProperty(product) {
  return sourceIsShopify(product) ? 'ip__shopify__body_html' : DESCRIPTION;
}
export function getProductTagsProperty(product) {
  return sourceIsShopify(product) ? 'ip__shopify__tags' : null;
}

/**
 * @deprecated
 * Keeping this getter around until issues with price can be resolved https://hubspot.slack.com/archives/C014ZCZ8H45/p1599672070034200?thread_ts=1595856593.006300&cid=C014ZCZ8H45
 */
export function getPriceProperty(lineItem) {
  const subject = getSubject(lineItem);
  if (sourceIsShopify(subject)) {
    return 'amount' in subject.properties ? 'amount' : PRICE;
  }
  return PRICE;
}